/**
 * What the LegacyDesigner shell may show and do on the route that mounts it.
 *
 * The V1 routes (`/proposal/:slug/design` and the service queue) turn every
 * feature on. A host without the V1 Proposal providers or a GraphQL client
 * turns the V1-only features off and supplies its own actions.
 *
 * Feature flags (each gates UI that needs V1-only providers or GraphQL):
 * - `designSwitcher`: the design version switcher panel and its top-bar icon.
 * - `designRequest`: Design Request UI — reject, escalate, pause and
 *   complete-and-next in the service queue, the Notes, Design Revision and
 *   Escalation drawers, and the top-bar notes icon.
 * - `editInfo`: the Edit Design Info action inside the design switcher.
 * - `mountType`: the top-bar mount type dropdown.
 * - `moduleDropdown`: the top-bar module selection dropdown.
 * - `resetLocation`: the reset-location pin button and its modal.
 * - `notifications`: the notification bar and its top-bar icon.
 *
 * Injected behavior:
 * - `backTarget`: where the top-bar back link goes. `v1-proposal` keeps the
 *   V1 behavior (service queue, brochure jump-to-slide, or proposal setup).
 * - `continuePhase`: what the phase list's Continue / Complete buttons run.
 * - `systemInfo`: where the system info panel reads annual production and
 *   energy offset. `brochure` reads the V1 brochure atoms; `provided` takes
 *   values from the host, including whether they are recalculating.
 * - `topBarAccessory`: optional host content for the top bar, where V1 shows
 *   its version icon (the V2 design version chip).
 */
import type { FC, ReactNode } from "react"
import { createContext, useContext } from "react"

export type DesignerFeatureFlags = {
  designSwitcher: boolean
  designRequest: boolean
  editInfo: boolean
  mountType: boolean
  moduleDropdown: boolean
  resetLocation: boolean
  notifications: boolean
}

export type DesignerBackTarget =
  | { kind: `custom`; label: string; navigate: VoidFunction }
  | { kind: `v1-proposal` }

export type ContinuePhase = (options: {
  completeAndNext: boolean
}) => Promise<void> | void

export type DesignerSystemInfoSource =
  | {
      kind: `provided`
      annualProductionKwh: number | null
      energyOffset: number | null
      isRecalculating: boolean
    }
  | { kind: `brochure` }

export type DesignerCapabilities = DesignerFeatureFlags & {
  backTarget: DesignerBackTarget
  continuePhase: ContinuePhase
  systemInfo: DesignerSystemInfoSource
  topBarAccessory?: ReactNode
}

export const V1_DESIGNER_FEATURES: DesignerFeatureFlags = {
  designSwitcher: true,
  designRequest: true,
  editInfo: true,
  mountType: true,
  moduleDropdown: true,
  resetLocation: true,
  notifications: true,
}

/** Every V1-only feature off: what a host without V1 providers starts from. */
export const SHELL_ONLY_DESIGNER_FEATURES: DesignerFeatureFlags = {
  designSwitcher: false,
  designRequest: false,
  editInfo: false,
  mountType: false,
  moduleDropdown: false,
  resetLocation: false,
  notifications: false,
}

const DesignerCapabilitiesContext = createContext<DesignerCapabilities | null>(
  null,
)

export const DesignerCapabilitiesProvider: FC<{
  capabilities: DesignerCapabilities
  children: ReactNode
}> = ({ capabilities, children }) => (
  <DesignerCapabilitiesContext.Provider value={capabilities}>
    {children}
  </DesignerCapabilitiesContext.Provider>
)

export const useDesignerCapabilities = (): DesignerCapabilities => {
  const capabilities = useContext(DesignerCapabilitiesContext)

  if (!capabilities) {
    throw new Error(`Designer capabilities not found`)
  }

  return capabilities
}
